import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from '../ui/toast';
import { Veld } from '../ui/ui';

function useAuthConfig() {
  return useQuery({ queryKey: ['authconfig'], queryFn: () => api.get<{ dev: boolean; alleenDemo?: boolean; demo?: string[]; entra: boolean; google: boolean; aanmelden: boolean }>('/auth/config') });
}

/** Inlogscherm in de huisstijl: merkpaneel links, formulier rechts (op mobiel alleen het formulier). */
function InlogLayout({ children }: { children: ReactNode }) {
  return (
    <div className="inlog">
      <aside className="inlog-merk">
        <img className="logo-groot" src="/logo-wit.svg" alt="bsmartr" />
        <h1>
          Plan je projecten en engineers <em>slimmer</em>.
        </h1>
        <p className="pitch">Van werkpakket tot weekrooster: bsmartr verdeelt het werk over de juiste mensen, rekening houdend met hun agenda, verlof en expertise.</p>
        <ul>
          <li><span className="stip">✓</span><span><b>Klassiek of agile</b> per werkpakket: PRINCE2-planning of sprints met een bord.</span></li>
          <li><span className="stip">✓</span><span><b>Agenda gekoppeld</b>: Outlook of Google, met vrij/bezet en automatisch afspraken.</span></li>
          <li><span className="stip">✓</span><span><b>Altijd zicht op budget</b>: uren per expertise, prognose en haalbaarheid van de livegang.</span></li>
        </ul>
        <div className="voet">© {new Date().getFullYear()} bsmartr</div>
      </aside>
      <main className="inlog-form">
        <div className="inlog-kaart">
          <img className="logo-klein" src="/logo.svg" alt="bsmartr" />
          {children}
        </div>
      </main>
    </div>
  );
}

const MsLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17z" />
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

export function Login() {
  const cfg = useAuthConfig().data;
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const demo = cfg?.demo?.length ? cfg.demo : ['beheerder@demo.example', 'planner@demo.example', 'pm@demo.example', 'noor.van.dalen@demo.example', 'thijs.kroonenberg@demo.example'];
  useEffect(() => {
    if (!email && cfg) setEmail(demo[0]);
  }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  const fout = new URLSearchParams(window.location.search).get('fout');
  const dev = async () => {
    try {
      const r = await api.post<{ kies?: boolean }>('/auth/dev', { email });
      // SPEC §17.2: meerdere organisaties → keuzescherm
      if (r.kies) window.history.replaceState(null, '', '/?kies=1');
      await qc.invalidateQueries();
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  return (
    <InlogLayout>
        <img className="icoon" src="/icon-192.png" alt="" />
        <div>
          <h2>Welkom terug</h2>
          <div className="mute">Log in om verder te gaan met je planning.</div>
        </div>
        {fout && <div className="paneel vlak" style={{ background: 'var(--rood-bg)' }}>{fout}</div>}
        {cfg?.entra && (
          <a className="ms-knop" href="/auth/login">
            <MsLogo /> Inloggen met Microsoft
          </a>
        )}
        {cfg?.entra && <p className="klein mute" style={{ textAlign: 'center', margin: '-4px 0 0' }}>Werk-, school- of privéaccount (Outlook.com, Office 365 Family)</p>}
        {cfg?.google && (
          <a className="ms-knop" href="/auth/google">
            <GoogleLogo /> Inloggen met Google
          </a>
        )}
        {cfg?.entra && cfg.aanmelden && <p className="klein mute">Eerste keer voor jouw organisatie? Log in met je werkaccount; je organisatie wordt dan automatisch aangemaakt en jij wordt beheerder.</p>}
        {cfg?.dev && (
          <div className="kolom">
            {(cfg.entra || cfg.google) && <div className="of">of</div>}
            <div className="klein vet mute">{cfg.alleenDemo ? 'Demo bekijken (alleen demo-accounts)' : 'Ontwikkel-/demo-login'}</div>
            <Veld label="E-mailadres">
              <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && dev()} />
            </Veld>
            <div className="rij wrap klein">
              {demo.map((x) => (
                <button key={x} className="knop klein" title={x} onClick={() => setEmail(x)}>
                  {x.split('@')[0]}
                </button>
              ))}
            </div>
            <button className="knop merk" onClick={dev} style={{ justifyContent: 'center' }}>
              Inloggen
            </button>
            {cfg.aanmelden && (
              <Link to="/aanmelden" className="klein">
                Nieuwe organisatie aanmelden
              </Link>
            )}
          </div>
        )}
        {cfg && !cfg.dev && !cfg.entra && !cfg.google && <p className="mute">Inloggen is nog niet geconfigureerd op deze server (zie README).</p>}
    </InlogLayout>
  );
}

export function Aanmelden() {
  const qc = useQueryClient();
  const [f, setF] = useState({ organisatie: '', naam: '', email: '' });
  const verstuur = async () => {
    try {
      await api.post('/auth/aanmelden', f);
      await qc.invalidateQueries();
      window.history.replaceState(null, '', '/');
    } catch (e) {
      toast((e as Error).message, 'fout');
    }
  };
  return (
    <InlogLayout>
        <h2>Nieuwe organisatie</h2>
        <p className="mute klein">Je start met een proefperiode van 30 dagen. Jij wordt beheerder en kunt daarna engineers, expertises en collega's toevoegen.</p>
        <Veld label="Naam organisatie">
          <input value={f.organisatie} onChange={(e) => setF({ ...f, organisatie: e.target.value })} />
        </Veld>
        <Veld label="Jouw naam">
          <input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} />
        </Veld>
        <Veld label="Zakelijk e-mailadres">
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Veld>
        <button className="knop merk" onClick={verstuur} style={{ justifyContent: 'center' }}>
          Organisatie aanmaken
        </button>
        <Link to="/" className="klein">
          ← Terug naar inloggen
        </Link>
    </InlogLayout>
  );
}
